import { useMenuRoomFit } from '@clickguard/design-system'
import { ChangeEvent, useState, useRef, useEffect } from 'react'

const AD_SPEND_OPTIONS = [
	{ value: 101, label: 'More than $100,000' },
	{ value: 100, label: 'Up to $100,000' },
	{ value: 75, label: 'Up to $75,000' },
	{ value: 50, label: 'Up to $50,000' },
	{ value: 40, label: 'Up to $40,000' },
	{ value: 30, label: 'Up to $30,000' },
	{ value: 20, label: 'Up to $20,000' },
	{ value: 15, label: 'Up to $15,000' },
	{ value: 10, label: 'Up to $10,000' },
	{ value: 5, label: 'Less than $5,000' }
]

interface AdSpendDropdownProps {
	value: number | null
	onChange: (value: number) => void
	onCustomSpendChange?: (value: number) => void
	label?: string
	showCustomInput?: boolean
	fontSize?: string
	error?: string
}

export const AdSpendDropdown = ({
	value,
	onChange,
	onCustomSpendChange,
	label,
	showCustomInput = true,
	fontSize = 'text-[16px]',
	error
}: AdSpendDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [customSpend, setCustomSpend] = useState('')
	const dropdownRef = useRef<HTMLDivElement>(null)
	// The menu hangs below the trigger inside pages that clip their overflow
	// (the onboarding layout), so on a short screen its lower options were cut
	// off and unreachable. The DS room fit caps the list to the room below the
	// trigger and scrolls the page to make room when there is too little.
	const triggerRef = useRef<HTMLButtonElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const { fitHeight } = useMenuRoomFit({
		open: isOpen,
		anchorRef: triggerRef,
		panelRef,
		gap: 4
	})

	const selectedOption = AD_SPEND_OPTIONS.find((opt) => opt.value === value)

	const isCustom = value === 101

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleSelect = (optionValue: number) => {
		onChange(optionValue)
		setIsOpen(false)
	}

	const handleCustomSpendChange = (e: ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, '')
		const num = parseInt(raw, 10) || 0
		setCustomSpend(num > 0 ? `${num.toLocaleString('en-US')}` : '')
		onCustomSpendChange?.(num)
	}

	return (
		<div className="flex flex-col w-full mb-3">
			{label && (
				<label className="text-[14px] leading-[18px] mb-[6px] font-medium text-t-google-ad font-grotesk">
					{label}
				</label>
			)}
			<div className="relative" ref={dropdownRef}>
				<button
					className={`w-full h-[46px] px-[12px] ${fontSize} leading-[24px] font-normal rounded-[8px] border bg-white flex items-center justify-between cursor-pointer text-left drop-shadow-[0px_1px_1px_rgba(16,24,40,0.05)] transition-colors ${
						isOpen ? 'border-brand' : 'border-t-gray-input'
					}`}
					onClick={() => setIsOpen(!isOpen)}
					ref={triggerRef}
					type="button"
				>
					<span
						className={
							selectedOption
								? 'text-t-coupon font-grotesk'
								: 'text-t-gray-hold font-grotesk'
						}
					>
						{selectedOption
							? selectedOption.label
							: 'Select your monthly ad spend'}
					</span>
					<svg
						className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
						fill="none"
						height="8"
						viewBox="0 0 12 8"
						width="12"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M1 1.5L6 6.5L11 1.5"
							style={{ stroke: 'var(--color-text-secondary)' }}
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="1.5"
						/>
					</svg>
				</button>

				{isOpen && (
					<div className="absolute z-50 w-full mt-1" ref={panelRef}>
						<div
							className="bg-popover border border-t-border rounded-lg shadow-dropShadow overflow-y-auto"
							style={{
								maxHeight: fitHeight === null ? 280 : Math.min(fitHeight, 280)
							}}
						>
							{AD_SPEND_OPTIONS.map((option) => (
								<button
									className={`w-full py-[10px] px-3 text-[14px] text-left cursor-pointer flex items-center justify-between transition-colors font-grotesk ${
										option.value === value
											? 'bg-t-step-inactive text-brand-tint-text font-medium'
											: 'text-t-default hover:bg-bg-plan-fill'
									}`}
									key={option.value}
									onClick={() => handleSelect(option.value)}
									type="button"
								>
									{option.label}
									{option.value === value && (
										<svg
											fill="none"
											height="10"
											viewBox="0 0 14 10"
											width="14"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M1 5L5 9L13 1"
												style={{ stroke: 'var(--color-brand)' }}
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
											/>
										</svg>
									)}
								</button>
							))}
						</div>
					</div>
				)}
			</div>

			{isCustom && showCustomInput && (
				<div className="flex flex-col gap-[6px] w-full mt-3">
					<label className="text-[14px] leading-[18px] font-medium text-t-google-ad font-grotesk">
						Fill in your monthly ad spend
					</label>
					<input
						className="w-full px-3 py-[10px] text-[16px] leading-[22px] font-normal rounded-[8px] border border-t-gray-input bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] focus:border-brand focus:outline-none text-t-coupon font-grotesk"
						onChange={handleCustomSpendChange}
						placeholder="$100,000.00"
						type="text"
						value={customSpend}
					/>
					<p className="text-[12px] leading-[16px] text-t-desc-gray font-grotesk">
						Min. $100,000.00
					</p>
					{!!error && (
						<p className="text-xs text-red-700 font-grotesk">{error}</p>
					)}
				</div>
			)}
		</div>
	)
}
